/**
 * PATH: packages/email-hub/src/feature/personal/PersonalSetupCard.tsx
 * FILE_ID: EMAILHUB-PERSONAL-SETUP-001
 * PURPOSE: Explicit opt-in for the caller's real Personal company tenant.
 * WHY: Personal must never be auto-created or inferred from mailbox access type.
 * FLOW: status query -> setup button -> idempotent API -> org switcher refresh.
 * RELATED: usePersonalData.ts; backend /api/organisations/personal/.
 */
import { Building2, Loader2, ShieldCheck } from 'lucide-react'

import {
  usePersonalOrganisation,
  useSetupPersonalOrganisation,
} from './usePersonalData'

export function PersonalSetupCard() {
  const status = usePersonalOrganisation()
  const setup = useSetupPersonalOrganisation()

  if (status.data) {
    return (
      <section
        role="status"
        className="mx-5 mt-4 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4"
      >
        <ShieldCheck className="mt-0.5 h-5 w-5 text-emerald-600" />
        <div>
          <h2 className="text-sm font-semibold text-emerald-900">
            Personal company is ready
          </h2>
          <p className="mt-1 text-sm text-emerald-700">
            Switch to Personal when you want to attach a private mailbox.
            Your company inboxes remain separate.
          </p>
        </div>
      </section>
    )
  }

  if (status.isLoading) {
    return (
      <div
        aria-label="Checking Personal company setup"
        className="mx-5 mt-4 h-24 animate-pulse rounded-xl bg-slate-100"
      />
    )
  }

  if (status.isError) {
    return (
      <div className="mx-5 mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4">
        <p className="text-sm text-rose-700">
          We could not check your Personal company setup.
        </p>
        <button
          type="button"
          onClick={() => void status.refetch()}
          className="mt-2 text-sm font-medium text-rose-800 underline"
        >
          Try again
        </button>
      </div>
    )
  }

  return (
    <section className="mx-5 mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-indigo-50 p-2 text-indigo-600">
          <Building2 className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-slate-900">
            Set up your Personal company
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Personal becomes a private company in your organisation switcher.
            Nothing moves into it until you explicitly attach a mailbox or email.
          </p>
          <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5" />
            Your existing company inboxes stay unchanged.
          </p>
          <button
            type="button"
            disabled={setup.isPending}
            onClick={() => setup.mutate()}
            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {setup.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Set up Personal
          </button>
          {setup.isError && (
            <p role="alert" className="mt-2 text-sm text-rose-600">
              {setup.error instanceof Error
                ? setup.error.message
                : 'Personal company setup failed. Please try again.'}
            </p>
          )}
        </div>
      </div>
    </section>
  )
}
